// Phase NAATI-MAX B2 — Shared landing for NAATI knowledge-base pages
// SSR-only, no client JS, schema-safe (no Review/AggregateRating).
import { Link } from "@tanstack/react-router";
import HubLeadCTA from "@/components/HubLeadCTA";
import NaatiClusterLinks from "@/components/NaatiClusterLinks";

export interface NaatiKbSection {
  heading: string;
  bullets?: string[];
  paragraph?: string;
  table?: { headers: string[]; rows: string[][] };
}

export interface NaatiKbProps {
  locale: "th" | "en";
  eyebrow: string;
  h1: string;
  lead: string;
  speakableAnswer: string;
  sections: NaatiKbSection[];
  faqs: { q: string; a: string }[];
  cta: { label: string; href: string };
}

export default function NaatiKnowledgePage(p: NaatiKbProps) {
  const isTh = p.locale === "th";
  const home = isTh ? "/translate/naati" : "/en/translate/naati";
  return (
    <main className="mx-auto max-w-4xl px-5 py-12 sm:px-8 sm:py-16">
      <nav className="mb-6 text-xs text-slate-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-amber-700">{isTh ? "หน้าแรก" : "Home"}</Link>
        <span className="mx-2">/</span>
        <Link to={home as any} className="hover:text-amber-700">NAATI</Link>
      </nav>
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700" data-speakable="true">
        {p.eyebrow}
      </div>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl" data-speakable="true">
        {p.h1}
      </h1>
      <p className="mt-4 text-lg leading-relaxed text-slate-700" data-speakable="true">
        {p.lead}
      </p>

      <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
          {isTh ? "คำตอบสั้น" : "Direct Answer"}
        </div>
        <p className="mt-2 text-base font-medium text-navy-deep direct-answer" data-speakable="true">
          {p.speakableAnswer}
        </p>
      </section>

      {p.sections.map((s, i) => (
        <section key={i} className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            {s.heading}
          </h2>
          {s.paragraph && (
            <p className="mt-3 text-base leading-relaxed text-slate-700">{s.paragraph}</p>
          )}
          {s.bullets && (
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {s.bullets.map((b, j) => (
                <li key={j} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          {s.table && (
            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>{s.table.headers.map((h, k) => <th key={k} className="px-4 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {s.table.rows.map((row, k) => (
                    <tr key={k} className="border-t border-slate-200">
                      {row.map((c, l) => <td key={l} className="px-4 py-2 align-top text-slate-700">{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}

      <section className="mt-12">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          {isTh ? "คำถามที่พบบ่อย" : "Frequently Asked Questions"}
        </h2>
        <div className="mt-4 space-y-3">
          {p.faqs.map((f, i) => (
            <details key={i} className="group rounded-xl border border-slate-200 bg-white p-4">
              <summary className="cursor-pointer text-sm font-semibold text-navy-deep">{f.q}</summary>
              <p className="mt-2 text-sm text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <NaatiClusterLinks />

      <HubLeadCTA
        locale={isTh ? "th" : "en"}
        service="NAATI Translation"
        title={isTh ? "พร้อมเริ่มงานแปล NAATI?" : "Ready to start your NAATI job?"}
      />
    </main>
  );
}
